import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { Panel } from "@/components/common/Panel";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CONFIG, PHASE, ROLE, STATE, SYNC } from "@/components/teamAccessLabels";

const HEAD = ["Konto", "Plattform-Secret", "Backend-Konfiguration", "Datenbank-Konto", "Aktiv", "Passwortwechsel offen", "2FA eingerichtet", "Secret-Übernahme", "Status"];
const yesno = (acc, field) => (acc.exists ? (acc[field] ? "Ja" : "Nein") : "—");

function Tag({ pair, testid }) {
  return <span data-testid={testid} className={`text-xs font-medium ${pair?.[1] || "text-fundio-muted"}`}>{pair?.[0] || "unbekannt"}</span>;
}

function KeyCell({ a }) {
  return (
    <div className="space-y-1">
      {a.keys.map((k) => (
        <div key={k.key} className="flex flex-wrap items-center gap-1.5">
          <code className="font-mono text-[11px] text-fundio-ink">{k.key}</code>
          <Tag pair={CONFIG[k.backend_config]} testid={`team-access-cfg-${a.id}-${k.key.toLowerCase()}`} />
          {k.source && k.source !== k.key && <span className="text-[11px] text-fundio-faint">über {k.source}</span>}
        </div>
      ))}
    </div>
  );
}

function SyncCell({ a, enabled }) {
  const s = a.secret_sync;
  if (!s) return <span className="text-xs text-fundio-muted">—</span>;
  const pair = !enabled && s.result === "pending" ? SYNC.ready : SYNC[s.result] || [s.result, "text-fundio-muted"];
  return (
    <div>
      <Tag pair={pair} testid={`team-access-sync-${a.id}`} />
      {s.applied_at && <div data-testid={`team-access-sync-at-${a.id}`} className="mt-0.5 text-[11px] text-fundio-faint">zuletzt übernommen {new Date(s.applied_at).toLocaleString("de-CH")}</div>}
    </div>
  );
}

function AccountRow({ a, enabled }) {
  const acc = a.account;
  return (
    <tr data-testid={`team-access-row-${a.id}`} className="align-top">
      <td className="px-4 py-3"><div className="font-medium text-fundio-ink">{a.name}</div><div className="text-[11px] text-fundio-faint">{ROLE[a.role]}</div></td>
      <td data-testid={`team-access-platform-${a.id}`} className="px-4 py-3 text-xs text-fundio-muted">nicht prüfbar</td>
      <td className="px-4 py-3"><KeyCell a={a} /></td>
      <td data-testid={`team-access-db-${a.id}`} className="px-4 py-3 text-xs">{acc.exists ? ROLE[acc.role] || acc.role : "kein Konto"}</td>
      <td data-testid={`team-access-active-${a.id}`} className="px-4 py-3 text-xs">{yesno(acc, "active")}</td>
      <td data-testid={`team-access-mcp-${a.id}`} className="px-4 py-3 text-xs">{yesno(acc, "must_change_password")}</td>
      <td data-testid={`team-access-2fa-${a.id}`} className="px-4 py-3 text-xs">{yesno(acc, "two_factor_enabled")}</td>
      <td className="px-4 py-3"><SyncCell a={a} enabled={enabled} /></td>
      <td className="px-4 py-3"><Tag pair={STATE[a.state]} testid={`team-access-state-${a.id}`} /><div className="mt-0.5 text-[11px] text-fundio-muted">{PHASE[a.phase] || a.phase}</div></td>
    </tr>
  );
}

function SyncFlag({ sync }) {
  if (!sync) return null;
  return (
    <p data-testid="team-access-sync-flag" className="border-b border-fundio-border px-5 py-2.5 text-xs text-fundio-muted">
      Secret-Übernahme: <span className={sync.enabled ? "font-medium text-status-success" : "font-medium text-fundio-ink"}>{sync.enabled ? "aktiv" : "aus"}</span>
      {" "}· Schalter <code className="font-mono text-[11px]">{sync.flag}=true</code>; neue Secret-Fassungen gelten nach dem nächsten Backend-Start (Live: Neu veröffentlichen) und beenden alle Sitzungen des Kontos.
    </p>
  );
}

function TeamAccessTable({ data }) {
  const enabled = Boolean(data.secret_sync?.enabled);
  return (
    <>
      <SyncFlag sync={data.secret_sync} />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1000px] text-sm">
          <thead><tr className="border-b border-fundio-border bg-fundio-subtle text-left text-[11px] font-semibold uppercase tracking-wider text-fundio-muted">{HEAD.map((h) => <th key={h} className="px-4 py-2.5">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-fundio-border">{data.accounts.map((a) => <AccountRow key={a.id} a={a} enabled={enabled} />)}</tbody>
        </table>
      </div>
      <ul data-testid="team-access-sources" className="space-y-1 border-t border-fundio-border px-5 py-3 text-[11px] text-fundio-muted">
        {Object.entries(data.sources).map(([k, v]) => <li key={k}>{v}</li>)}
      </ul>
    </>
  );
}

export function TeamAccessPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/users/team-access-status").then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);
  return (
    <Panel title="Team-Zugänge (wertfrei)" subtitle={data ? `Quelle: laufendes Backend · ${data.environment}` : undefined} testid="team-access-panel" noPad>
      {error ? <p data-testid="team-access-error" className="px-5 py-4 text-sm text-status-danger">{error}</p>
        : !data ? <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-fundio-gold" /></div>
          : <TeamAccessTable data={data} />}
    </Panel>
  );
}
